'use client'

import { useIsIOSClient } from '@/hooks/common/use-device-type'
import {
  openInExternalBrowser,
  useInAppBrowser,
} from '@/hooks/use-in-app-browser'
import { useZaloEmbedded } from '@/hooks/use-zalo-embedded'
import { ArrowRight, Clock, Compass, RefreshCw } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'

import { Button } from '@/components/ui/button'
import { ImageHelper } from '@/lib/helpers/image.helper'

function UnsupportedInAppBrowser() {
  const isIOS = useIsIOSClient()

  const instruction =
    'Nếu nút trên không tự mở được, vui lòng nhấn vào biểu tượng menu (⋮) ở góc trên và chọn "Mở bằng trình duyệt khác" để chọn trình duyệt bạn muốn.'

  return (
    <div className='from-primary-50 to-primary-50 flex min-h-screen items-center justify-center bg-gradient-to-br via-white px-6'>
      <div className='w-full max-w-md space-y-4 text-center'>
        <div className='mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100'>
          <Compass className='h-8 w-8 text-primary-500' />
        </div>
        <div>
          <h1 className='text-lg font-bold text-gray-900 sm:text-xl'>
            Trình duyệt trong ứng dụng chưa được hỗ trợ
          </h1>
          <p className='mt-2 text-sm text-gray-600'>
            Vui lòng mở trang này bằng trình duyệt bên ngoài (Safari, Chrome,
            Firefox,...) để trải nghiệm đầy đủ tính năng đổi trả hàng.
          </p>
        </div>

        {isIOS ? (
          <div className='mx-auto w-full max-w-xs'>
            <Image
              src={ImageHelper.getImageUrl(
                '/image/2026/09/03/20260903-133059.png'
              )}
              alt='Hướng dẫn mở bằng Safari'
              width={400}
              height={600}
              className='h-auto w-full rounded-xl'
            />
          </div>
        ) : (
          <>
            <div className='mx-auto w-full max-w-xs'>
              <Image
                src={ImageHelper.getImageUrl(
                  '/image/2026/09/03/20260903-133832.png'
                )}
                alt='Hướng dẫn mở bằng trình duyệt'
                width={400}
                height={600}
                className='h-auto w-full rounded-xl'
              />
            </div>
            <Button
              size='large'
              onClick={openInExternalBrowser}
              className='h-12 w-full bg-primary-500 text-sm font-semibold text-white hover:bg-primary-600'
            >
              Mở bằng trình duyệt
            </Button>
            <p className='text-xs text-gray-400'>{instruction}</p>
          </>
        )}
      </div>
    </div>
  )
}

const OMI_FORM_URL = 'https://form.omicrm.io/55551p112pi1e517p99h17h1'

function SectionDivider({ label }: { label: string }) {
  return (
    <div className='flex items-center justify-center gap-2'>
      <span className='h-0.5 w-10 bg-neutral-900 lg:bg-white' />
      <span className='pb-1 font-criteria text-lg tracking-[-0.02em] text-neutral-900 lg:text-white'>
        {label}
      </span>
      <span className='h-0.5 w-10 bg-neutral-900 lg:bg-white' />
    </div>
  )
}

const needButtonClass =
  'h-[50px] w-full text-base font-normal [&_svg]:size-5 lg:bg-white lg:text-neutral-900 lg:hover:bg-neutral-100'

function NeedOption({
  href,
  label,
  note,
  external,
}: {
  href: string
  label: string
  note: string
  external?: boolean
}) {
  return (
    <div className='flex flex-col items-center gap-1'>
      <Button asChild variant='primary' className={needButtonClass}>
        {external ? (
          <a href={href} target='_blank' rel='noopener noreferrer'>
            {label}
            <ArrowRight strokeWidth={1.5} />
          </a>
        ) : (
          <Link href={href}>
            {label}
            <ArrowRight strokeWidth={1.5} />
          </Link>
        )}
      </Button>
      <p className='text-center text-sm text-neutral-700 lg:text-neutral-100'>
        {note}
      </p>
    </div>
  )
}

export default function ExchangeWelcomePage() {
  const isZaloEmbedded = useZaloEmbedded()
  const isInAppBrowser = useInAppBrowser()

  // Chặn các webview trong app (Zalo/Messenger/...) ngoại trừ luồng Zalo Mini
  // App chính thức (?source=zalo), vốn đã tự ẩn các nút dẫn tới form ngoài.
  if (isInAppBrowser && !isZaloEmbedded) {
    return <UnsupportedInAppBrowser />
  }

  return (
    <div className='flex min-h-[calc(100vh-72px)] items-center justify-center lg:px-6 lg:py-10'>
      {/* Mobile: 1 cột (tiêu đề → minh họa → nhu cầu → thông tin).
          Desktop: 2 cột, panel nhu cầu nền xanh bên phải */}
      <div className='grid w-full max-w-[1240px] grid-cols-1 overflow-hidden lg:grid-cols-[1fr_400px] lg:grid-rows-[auto_auto_1fr] lg:rounded-3xl lg:shadow-[0_12px_50px_-17px_rgba(0,0,0,0.25)]'>
        {/* Header */}
        <div className='px-5 pt-6 lg:px-8 lg:pt-8'>
          <h1 className='font-criteria text-2xl font-bold leading-[35px] text-gray-900 lg:text-4xl lg:leading-10'>
            Yêu cầu <span className='text-primary-500'>Đổi hàng</span>
            {isZaloEmbedded ? (
              <>
                {' '}
                <span className='text-gray-500'>và</span>
                <br className='lg:hidden' /> Trả hàng/ Hoàn tiền
              </>
            ) : null}
          </h1>
          <p className='mt-2 text-sm text-gray-600 lg:text-xl'>
            Coolmate rất xin lỗi về sự bất tiện mà bạn gặp phải 😔
          </p>
        </div>

        {/* Illustration */}
        <div className='flex justify-center px-5 pt-4 lg:px-8'>
          <Image
            src={ImageHelper.getImageUrl('/image/June2025/OBJECTS.png')}
            alt='Minh họa đổi trả hàng'
            width={352}
            height={256}
            className='h-auto w-[220px] lg:w-[352px]'
            priority
          />
        </div>

        {/* Needs panel */}
        <div className='flex flex-col gap-10 px-5 pt-6 lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:bg-primary-500 lg:px-6 lg:pb-6 lg:pt-12'>
          <div className='hidden flex-col items-center text-center lg:flex'>
            <Image
              src='/images/logo-symbol-white.svg'
              alt=''
              width={84}
              height={80}
            />
            <h2 className='mt-6 font-criteria text-[28px] font-semibold leading-[41px] tracking-[-0.02em] text-white'>
              Chọn nhu cầu của bạn
            </h2>
            <p className='text-sm text-neutral-100'>
              Chúng tôi hỗ trợ bạn một cách tốt nhất
            </p>
          </div>

          <div className='flex flex-col gap-3 md:gap-4'>
            <SectionDivider label='Đổi hàng' />
            <NeedOption
              href='/doi-tra/verify'
              label='Đổi size - Đổi màu'
              note='(Áp dụng đơn mua qua Website, Shopee, Tiktok, Lazada)'
            />
            {!isZaloEmbedded && (
              <NeedOption
                href={OMI_FORM_URL}
                external
                label='Các trường hợp khác'
                note='(Đổi mẫu, đổi lần 2, đơn mua qua Zalo, Facebook...)'
              />
            )}
          </div>

          {!isZaloEmbedded && (
            <div className='flex flex-col gap-4'>
              <SectionDivider label='Trả hàng' />
              <Button
                asChild
                variant='outline'
                className='h-[50px] w-full text-base font-normal lg:border-white lg:bg-white/10 lg:text-white lg:hover:bg-white/20 [&_svg]:size-5'
              >
                <a
                  href={OMI_FORM_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Trả hàng hoàn tiền
                  <ArrowRight strokeWidth={1.5} />
                </a>
              </Button>
            </div>
          )}
        </div>

        {/* Info cards */}
        <div className='flex flex-col gap-3 px-5 pb-8 pt-6 lg:px-8 lg:pb-8'>
          <div className='flex gap-3 rounded-lg border border-l-4 border-primary-500 p-4 shadow-sm'>
            <div className='flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-100'>
              <RefreshCw className='size-5 text-primary-500' />
            </div>
            <div className='min-w-0 flex-1'>
              <h3 className='font-criteria font-semibold text-gray-900'>
                Thời gian đổi hàng
              </h3>
              <p className='text-sm text-gray-600'>
                Coolmate giao hàng mới và thu hàng cũ trong thời gian từ{' '}
                <span className='font-semibold text-primary-500'>
                  2 - 5 ngày
                </span>{' '}
                (không tính thứ 7 &amp; Chủ Nhật) sau khi yêu cầu được CSKH xác
                nhận.
              </p>
            </div>
          </div>

          <div className='flex gap-3 rounded-lg border border-l-4 border-green-500 bg-green-50/50 p-4 shadow-sm'>
            <div className='flex size-10 shrink-0 items-center justify-center rounded-full bg-green-100'>
              <Clock className='size-5 text-green-600' />
            </div>
            <div className='min-w-0 flex-1'>
              <h3 className='font-criteria font-semibold text-gray-900'>
                Thời gian xử lý Hoàn tiền
              </h3>
              <ul className='list-disc pl-4 text-sm text-gray-600'>
                <li>
                  Lệnh hoàn tiền được thực hiện vào{' '}
                  <span className='font-semibold'>Thứ 2 và Thứ 5</span> hằng
                  tuần.
                </li>
                <li>
                  Sau khi lệnh hoàn tiền được thực hiện, bạn sẽ nhận được tiền
                  trong khoảng{' '}
                  <span className='font-semibold'>24 giờ làm việc</span> (không
                  tính Thứ 7 &amp; Chủ Nhật), tùy theo ngân hàng.
                </li>
                <li>
                  Số tiền hoàn lại là giá trị sản phẩm sau khi đã trừ{' '}
                  <span className='font-semibold'>
                    25.000 VNĐ phí ship hàng
                  </span>{' '}
                  (nếu có)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
